'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'next/navigation';
import type { SpecialProps } from './types';

const SCRIPT_URL = 'https://widget.protectgroup.com/widget.js';
const MESSAGE_SOURCE = 'protect-group-classic';

function languageFor(value: string | undefined): string {
  const language = value?.split('-')[0] ?? 'es';
  return language === 'ca' || language === 'eu' ? 'es' : language;
}

function frameDocument(config: Record<string, unknown>, baseTotal: number): string {
  const serialized = JSON.stringify(config).replace(/</g, '\\u003c');
  return `<!doctype html><html><head><meta charset="utf-8"><style>
body { margin: 0; }
div#refundable-container { --pg-border: #b4b4b4; }
.styles-refundTitle-VrbjJ h1, .styles-refundTitle-VrbjJ h3 { color: #29abe2; }
</style></head><body>
<span id="booking-cost" style="display:none">${baseTotal}</span>
<div id="refundable-container"></div>
<script>
(function () {
  // The classic SDK restores sold from storage even in SELECT mode.
  // A same-origin iframe shares sessionStorage: clear only the SDK's own keys.
  try {
    ['pg-widget:product', 'pg-widget:quote', 'pg-widget:request',
     'pg-widget:refundable-product', 'pg-widget:check', 'pg-widget:session']
      .forEach(function (key) { window.sessionStorage.removeItem(key); });
  } catch (_) { /* Storage can be unavailable in restricted browser contexts. */ }
  var interacted = false;
  function send(type, payload) {
    window.parent.postMessage(Object.assign({ source: '${MESSAGE_SOURCE}', type: type }, payload || {}), '*');
  }
  function markChoice(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('a')) return;
    if (target.matches('input[type="radio"]')) { interacted = true; return; }
    // The SDK also selects an option when its whole header is clicked.
    var container = document.getElementById('refundable-container');
    for (var node = target; node && node !== container; node = node.parentElement) {
      if (node.querySelectorAll('input[type="radio"]').length === 1) { interacted = true; return; }
    }
  }
  document.addEventListener('pointerdown', markChoice, true);
  document.addEventListener('keydown', markChoice, true);
  document.addEventListener('PgWidgetProtectionChange', function (event) {
    if (!event.detail || typeof event.detail.protectionSelected !== 'boolean') return;
    send('protection-change', { protectionSelected: event.detail.protectionSelected, userInteracted: interacted });
    interacted = false;
  });
  document.addEventListener('PgWidgetLoaded', function () { send('loaded'); });
  window._pgr = window._pgr || function () { (window._pgr.q = window._pgr.q || []).push(arguments); };
  window._pgr('init', ${serialized});
  var script = document.createElement('script');
  script.id = '_pgr';
  script.src = '${SCRIPT_URL}';
  document.head.appendChild(script);
  if (window.ResizeObserver) new ResizeObserver(function () { send('resize', { height: document.documentElement.scrollHeight }); }).observe(document.body);
}());
</script></body></html>`;
}

export default function RefundableBookingPGClassic({ question, baseTotal, eventCurrency, onChange }: SpecialProps) {
  const params = useParams<{ lang?: string }>();
  const positive = useMemo(
    () => (question.questionOptionList ?? []).find(option => option.enabled !== false && (option.price ?? 0) > 0),
    [question.questionOptionList],
  );
  const negative = useMemo(
    () => (question.questionOptionList ?? []).find(option => option.enabled !== false && (option.price ?? 0) === 0),
    [question.questionOptionList],
  );
  const config = useMemo(() => ({
    environment: ['prod', 'production'].includes(process.env.NEXT_PUBLIC_PROTECTGROUP_ENV ?? '') ? 'prod' : 'test',
    vendorCode: process.env.NEXT_PUBLIC_PROTECTGROUP_VENDOR_CODE ?? '',
    currencyCode: eventCurrency || 'EUR',
    languageCode: languageFor(params?.lang),
    eventDateFormat: 'DD/MM/YYYY',
    refundableOptions: {
      premiumRate: Math.trunc(positive?.price ?? 0),
      offeringMethod: 'SELECT',
      productCode: 'TKT',
    },
    refundableSelectors: { bookingCost: 'booking-cost' },
  }), [eventCurrency, params?.lang, positive?.price]);
  const frameKey = useMemo(() => JSON.stringify({ config, baseTotal }), [config, baseTotal]);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastSelection = useRef<boolean | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    lastSelection.current = null;
    onChangeRef.current(null);
  }, [frameKey]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const data = event.data;
      if (!data || data.source !== MESSAGE_SOURCE) return;
      if (data.type === 'resize' && typeof data.height === 'number' && Number.isFinite(data.height)) {
        frame.style.height = `${Math.max(180, Math.ceil(data.height))}px`;
        return;
      }
      if (data.type !== 'protection-change' || typeof data.protectionSelected !== 'boolean' || data.userInteracted !== true) return;
      if (lastSelection.current === data.protectionSelected) return;
      lastSelection.current = data.protectionSelected;
      const option = data.protectionSelected ? positive : negative;
      if (option) onChange(String(option.questionOptionId));
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [negative, onChange, positive]);

  return (
    <iframe
      key={frameKey}
      ref={iframeRef}
      title="Protect Group refundable booking"
      srcDoc={frameDocument(config, baseTotal)}
      style={{ width: '100%', minHeight: 180, border: 0 }}
    />
  );
}
